import { memo, useMemo } from "react";
import {
  ROUTE_STROKE_WIDTH,
  ROUTE_SELECTED_STROKE_WIDTH,
  ROUTE_SELECTED_BORDER_WIDTH,
  ROUTE_SELECTED_CASING_COLOR,
  ROUTE_SELECTED_CHAIN_COLOR,
  ROUTE_SELECTED_CHAIN_WIDTH,
  ROUTE_SELECTED_CHAIN_DASH,
  ROUTE_LIVE_CASING_COLOR,
  ROUTE_LIVE_CASING_OPACITY,
  ROUTE_LIVE_CASING_WIDTH,
  ROUTE_LIVE_STROKE_WIDTH,
} from "./constants";
import type { CircuitRouteProps } from "./types";

const DEFAULT_RGB: [number, number, number] = [255, 255, 0];

const PATH_PROPS = {
  fill: "none",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  vectorEffect: "non-scaling-stroke",
} as const;

const HOVER_STYLE = { pointerEvents: "stroke", cursor: "pointer" } as const;

export const CircuitRoute = memo(function CircuitRoute({
  waypoints,
  rgb = DEFAULT_RGB,
  isSelected = false,
  isLive = false,
  opacity = 0.9,
  isHoverEnabled = true,
  strokeScale = 1,
}: CircuitRouteProps) {
  const d = useMemo(
    () =>
      waypoints
        .map((pt, i) => `${i === 0 ? "M" : "L"}${pt.x},${pt.y}`)
        .join(" "),
    [waypoints],
  );

  if (waypoints.length < 2) return null;

  const color = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;

  const restingWidth = isSelected
    ? ROUTE_SELECTED_STROKE_WIDTH
    : ROUTE_STROKE_WIDTH;
  const strokeWidth =
    (isLive ? ROUTE_LIVE_STROKE_WIDTH : restingWidth) * strokeScale;

  const hoverStyle = isHoverEnabled ? HOVER_STYLE : undefined;
  const showSelectedChain = isSelected && !isLive;

  return (
    <>
      {isLive && (
        <path
          data-testid="circuit-route-live-casing"
          d={d}
          stroke={ROUTE_LIVE_CASING_COLOR}
          strokeWidth={ROUTE_LIVE_CASING_WIDTH * strokeScale}
          opacity={ROUTE_LIVE_CASING_OPACITY}
          style={hoverStyle}
          {...PATH_PROPS}
        />
      )}
      {isSelected && (
        <path
          data-testid="circuit-route-casing"
          d={d}
          stroke={ROUTE_SELECTED_CASING_COLOR}
          strokeWidth={ROUTE_SELECTED_BORDER_WIDTH}
          opacity={opacity}
          style={hoverStyle}
          {...PATH_PROPS}
        />
      )}
      <path
        data-testid="circuit-route"
        d={d}
        stroke={color}
        strokeWidth={strokeWidth}
        opacity={opacity}
        style={hoverStyle}
        {...PATH_PROPS}
      />
      {showSelectedChain && (
        <path
          data-testid="circuit-route-chain"
          className="route-selected-chain"
          d={d}
          stroke={ROUTE_SELECTED_CHAIN_COLOR}
          strokeWidth={ROUTE_SELECTED_CHAIN_WIDTH * strokeScale}
          strokeDasharray={ROUTE_SELECTED_CHAIN_DASH}
          opacity={opacity}
          style={hoverStyle}
          {...PATH_PROPS}
        />
      )}
    </>
  );
});
